import { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import AdminSideMenu from "../components/AdminSideMenu";
import AdminTopMenu from "../components/AdminTopMenu/AdminTopMenu";
import styles from "./UsersScreen.module.css";
import { getTranscriptions, TranscriptionItem } from "../services/transcriptionService";

const toIsoFromDatetimeLocal = (val: string) => {
  if (!val) return undefined;
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
};

const isRtlLanguage = (lang?: string) => {
  const code = (lang || "").toLowerCase();
  return code.startsWith("ar") || code.startsWith("ur") || code.startsWith("fa") || code.startsWith("ps");
};

const escapeHtml = (unsafe: string) => {
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

/**
 * For Arabic/Urdu/Persian/Pashto, client-side PDF libraries often fail without embedding a Unicode font.
 * Browser "Print to PDF" preserves shaping and direction using system fonts.
 */
const printToPdf = (title: string, text: string, rtl: boolean) => {
  const w = window.open("", "_blank");
  if (!w) return;

  const safeTitle = escapeHtml(title);
  const safeText = escapeHtml(text);
  const dir = rtl ? "rtl" : "ltr";
  const align = rtl ? "right" : "left";

  w.document.open();
  w.document.write(`<!doctype html>
<html lang="en" dir="${dir}">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${safeTitle}</title>
    <style>
      @page { margin: 18mm; }
      body {
        font-family: "Noto Naskh Arabic", "Noto Nastaliq Urdu", "Segoe UI", Arial, sans-serif;
        direction: ${dir};
        text-align: ${align};
        font-size: 16px;
        line-height: 1.9;
        color: #111;
      }
      .content {
        white-space: pre-wrap;
        unicode-bidi: plaintext;
      }
      .toolbar {
        display: flex;
        gap: 8px;
        margin-bottom: 12px;
      }
      @media print {
        .toolbar { display: none; }
      }
      button {
        padding: 10px 14px;
        border-radius: 8px;
        border: 1px solid #ddd;
        background: #1f8f3a;
        color: white;
        cursor: pointer;
      }
      button.secondary {
        background: #f3f3f3;
        color: #111;
      }
    </style>
  </head>
  <body>
    <div class="toolbar">
      <button onclick="window.print()">Save as PDF</button>
      <button class="secondary" onclick="window.close()">Close</button>
    </div>
    <div class="content">${safeText}</div>
  </body>
</html>`);
  w.document.close();

  // Give the browser a moment to layout, then open print dialog.
  setTimeout(() => {
    try { w.focus(); w.print(); } catch { /* ignore */ }
  }, 250);
};

type Paragraph = { key: string; text: string; startAt?: string; endAt?: string; fromLanguage?: string };

function buildParagraphs(itemsAsc: TranscriptionItem[]): Paragraph[] {
  const GAP_MS = 60 * 1000; // new paragraph if gap > 60s
  const paras: Paragraph[] = [];
  let current: Paragraph | null = null;
  let lastTs: number | null = null;

  for (const it of itemsAsc) {
    const t = new Date(it.occurredAt).getTime();
    const text = (it.text || "").trim();
    if (!text) continue;

    const shouldBreak = lastTs != null && t - lastTs > GAP_MS;
    if (!current || shouldBreak) {
      if (current) paras.push(current);
      current = {
        key: it.id,
        text,
        startAt: it.occurredAt,
        endAt: it.occurredAt,
        fromLanguage: it.fromLanguage,
      };
    } else {
      const joiner = /[.?!۔]\s*$/.test(current.text) ? " " : " ";
      current.text = `${current.text}${joiner}${text}`.trim();
      current.endAt = it.occurredAt;
    }
    lastTs = t;
  }
  if (current) paras.push(current);
  return paras;
}

const TranscriptionRoomDetailScreen: FunctionComponent = () => {
  const { roomId: roomIdParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const roomId = decodeURIComponent(roomIdParam || "");
  const roomName = (location.state as any)?.roomName as string | undefined;

  const [items, setItems] = useState<TranscriptionItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [q, setQ] = useState("");
  const [startLocal, setStartLocal] = useState("");
  const [endLocal, setEndLocal] = useState("");

  const load = useCallback(async () => {
    if (!roomId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getTranscriptions({
        page: 1,
        limit: 200,
        roomId,
        q: q.trim() || undefined,
        startDate: toIsoFromDatetimeLocal(startLocal),
        endDate: toIsoFromDatetimeLocal(endLocal),
      });
      setItems(data.items || []);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.response?.data?.message || e.message || "Failed to load transcriptions");
    } finally {
      setLoading(false);
    }
  }, [endLocal, q, roomId, startLocal]);

  useEffect(() => {
    // Default to last 30 minutes
    const end = new Date();
    const start = new Date(end.getTime() - 30 * 60 * 1000);
    setStartLocal(start.toISOString().slice(0, 16));
    setEndLocal(end.toISOString().slice(0, 16));
  }, []);

  // After defaults are set (or when filters change), load data
  useEffect(() => {
    if (!roomId) return;
    if (!startLocal || !endLocal) return;
    load();
  }, [endLocal, load, roomId, startLocal]);

  const itemsAsc = useMemo(() => {
    return [...items].sort((a, b) => new Date(a.occurredAt).getTime() - new Date(b.occurredAt).getTime());
  }, [items]);

  const paragraphs = useMemo(() => buildParagraphs(itemsAsc), [itemsAsc]);
  const fromLanguage = items[0]?.fromLanguage;
  const rtl = isRtlLanguage(fromLanguage);

  const downloadPdf = () => {
    const textOnly = paragraphs.map(p => p.text).filter(Boolean).join("\n\n").trim();
    if (!textOnly) return;

    // RTL languages need browser print-to-pdf for correct shaping
    if (rtl) {
      const title = roomName ? `${roomName} — ${roomId}` : roomId;
      printToPdf(title, textOnly, rtl);
      return;
    }

    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 48;
    const maxWidth = pageWidth - margin * 2;
    const lineHeight = 18;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);

    const lines = doc.splitTextToSize(textOnly, maxWidth);
    let y = margin;
    const x = rtl ? pageWidth - margin : margin;

    for (const line of lines) {
      if (y > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
      doc.text(String(line), x, y, { align: rtl ? "right" : "left", maxWidth });
      y += lineHeight;
    }

    const ts = new Date();
    const pad = (n: number) => String(n).padStart(2, "0");
    const stamp = `${ts.getFullYear()}${pad(ts.getMonth() + 1)}${pad(ts.getDate())}-${pad(ts.getHours())}${pad(ts.getMinutes())}`;
    doc.save(`transcription-${roomId}-${stamp}.pdf`);
  };

  const onLoadRecent = () => {
    const end = new Date();
    const start = new Date(end.getTime() - 30 * 60 * 1000);
    setStartLocal(start.toISOString().slice(0, 16));
    setEndLocal(end.toISOString().slice(0, 16));
    setTimeout(() => load(), 0);
  };

  return (
    <div>
      <AdminSideMenu />
      <AdminTopMenu />
      <div className={styles.container}>
        <div className={styles.mainContent}>
          <div className={styles.header} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <h1 className={styles.headerTitle}>
              {roomName ? `${roomName} — ` : ""}{roomId}
            </h1>
            <button className={styles.pageButton} onClick={() => navigate("/transcriptions")}>Back</button>
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
            <input
              type="text"
              placeholder="Search inside transcriptions..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className={styles.searchInput}
              style={{ marginBottom: 0, width: 260 }}
            />
            <input
              type="datetime-local"
              value={startLocal}
              onChange={(e) => setStartLocal(e.target.value)}
              className={styles.searchInput}
              style={{ marginBottom: 0, width: 220 }}
            />
            <input
              type="datetime-local"
              value={endLocal}
              onChange={(e) => setEndLocal(e.target.value)}
              className={styles.searchInput}
              style={{ marginBottom: 0, width: 220 }}
            />
            <button className={styles.addUserButton} onClick={load}>Search</button>
            <button className={styles.pageButton} onClick={onLoadRecent}>Last 30 min</button>
          </div>

          {error && <div className={styles.errorContainer}><p>{error}</p></div>}

          <div style={{ border: "1px solid #e6e6e6", borderRadius: 10, padding: 16, background: "#fff" }}>
            {loading ? (
              <div className={styles.loadingContainer}>
                <div className={styles.loadingSpinner}></div>
                <p>Loading transcriptions...</p>
              </div>
            ) : (
              <>
                {paragraphs.length === 0 ? (
                  <div style={{ padding: 8, opacity: 0.7 }}>No transcriptions found for this time range.</div>
                ) : (
                  <div style={{ maxHeight: "calc(100vh - 320px)", overflow: "auto" }}>
                    {paragraphs.map((p) => (
                      <div key={p.key} style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 12, opacity: 0.65, marginBottom: 6 }}>
                          {p.startAt ? new Date(p.startAt).toLocaleString() : ""}
                          {p.endAt && p.endAt !== p.startAt ? ` → ${new Date(p.endAt).toLocaleString()}` : ""}
                        </div>
                        <p style={{ margin: 0, lineHeight: 1.8, fontSize: 18, direction: rtl ? "rtl" : "ltr", textAlign: rtl ? "right" : "left" }}>
                          {p.text}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Download PDF: text only (no timestamps) */}
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                  <button
                    className={styles.addUserButton}
                    onClick={downloadPdf}
                    disabled={paragraphs.length === 0}
                    title="Download PDF (text only)"
                  >
                    Download PDF
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TranscriptionRoomDetailScreen;

